import { AlertTriangle, Check, RefreshCw, Slash, X } from "lucide-react";

import { Modal } from "@/components/Modal";
import type { LiveListing } from "@/context/AppContext";
import type { EndReason } from "@/features/ebayListings";

const END_REASONS: { label: string; value: EndReason; description: string }[] =
  [
    {
      label: "Not available",
      value: "NotAvailable",
      description: "Item is no longer available",
    },
    {
      label: "Lost or broken",
      value: "LostOrBroken",
      description: "Item was lost or damaged",
    },
    {
      label: "Incorrect details",
      value: "Incorrect",
      description: "Listing has wrong info; will relist",
    },
    {
      label: "Sold elsewhere",
      value: "OtherListingError",
      description: "Sold on another channel",
    },
  ];

const DURATIONS: (number | "GTC")[] = [1, 3, 5, 7, 10, 30, "GTC"];

interface BulkEndProps {
  open: boolean;
  targets: LiveListing[];
  reason: EndReason | null;
  onReasonChange: (r: EndReason) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export function BulkEndModal({
  open,
  targets,
  reason,
  onReasonChange,
  onClose,
  onSubmit,
}: BulkEndProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Bulk end listings"
      panelClassName="w-[min(480px,92vw)] max-h-[85vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">
          End {targets.length} listing{targets.length === 1 ? "" : "s"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-3 overflow-y-auto p-4">
        <p className="text-xs text-muted-foreground">
          Each selected listing will be ended on eBay using the same reason.
          Status will update as each request finishes.
        </p>
        <div className="space-y-2">
          {END_REASONS.map((opt) => {
            const checked = reason === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onReasonChange(opt.value)}
                className={
                  checked
                    ? "flex w-full items-start gap-3 rounded-md border border-destructive bg-destructive/10 p-3 text-left"
                    : "flex w-full items-start gap-3 rounded-md border border-border bg-muted p-3 text-left hover:border-muted-foreground"
                }
              >
                <span
                  className={
                    checked
                      ? "mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-white"
                      : "mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground"
                  }
                >
                  {checked && <Check className="h-3 w-3" />}
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-medium text-foreground">
                    {opt.label}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {opt.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!reason}
          className="inline-flex items-center gap-1 rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-50"
        >
          <Slash className="h-3.5 w-3.5" />
          End {targets.length}
        </button>
      </div>
    </Modal>
  );
}

interface BulkExtendProps {
  open: boolean;
  targets: LiveListing[];
  duration: number | "GTC";
  onDurationChange: (d: number | "GTC") => void;
  onClose: () => void;
  onSubmit: () => void;
}

export function BulkExtendModal({
  open,
  targets,
  duration,
  onDurationChange,
  onClose,
  onSubmit,
}: BulkExtendProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Bulk extend listings"
      panelClassName="w-[min(440px,92vw)] max-h-[85vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">
          Extend {targets.length} listing{targets.length === 1 ? "" : "s"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-3 p-4">
        <p className="text-xs text-muted-foreground">
          New duration replaces the existing end time on eBay. GTC = Good Till
          Cancelled (no end time, fixed-price only).
        </p>
        <div className="grid grid-cols-4 gap-2">
          {DURATIONS.map((d) => {
            const active = duration === d;
            return (
              <button
                key={String(d)}
                type="button"
                onClick={() => onDurationChange(d)}
                className={
                  active
                    ? "rounded-md bg-secondary px-3 py-2 text-xs font-semibold text-secondary-foreground"
                    : "rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
                }
              >
                {d === "GTC" ? "GTC" : `${d}d`}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          className="rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:opacity-90"
        >
          Extend {targets.length}
        </button>
      </div>
    </Modal>
  );
}

interface BulkRelistProps {
  open: boolean;
  targets: LiveListing[];
  onClose: () => void;
  onSubmit: () => void;
}

export function BulkRelistModal({
  open,
  targets,
  onClose,
  onSubmit,
}: BulkRelistProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Bulk relist listings"
      panelClassName="w-[min(440px,92vw)] max-h-[85vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">
          Relist {targets.length} listing{targets.length === 1 ? "" : "s"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-2 p-4 text-sm text-muted-foreground">
        <p>
          Each selected ended listing will be relisted on eBay using its
          original photos, description, shipping, and duration.
        </p>
        <p className="text-xs">
          Price carries over: auctions keep their starting bid / reserve /
          Buy-It-Now; fixed-price uses the previous asking price.
        </p>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          className="inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:opacity-90"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Relist {targets.length}
        </button>
      </div>
    </Modal>
  );
}

export interface BulkProgressResult {
  listingId: string;
  title: string;
  ok: boolean;
  error?: string;
}

interface BulkProgressProps {
  open: boolean;
  action: "end" | "extend" | "relist";
  total: number;
  done: number;
  running: boolean;
  results: BulkProgressResult[];
  onClose: () => void;
}

export function BulkProgressModal({
  open,
  action,
  total,
  done,
  running,
  results,
  onClose,
}: BulkProgressProps) {
  const failed = results.filter((r) => !r.ok).length;
  const succeeded = results.length - failed;
  const verb =
    action === "end"
      ? "Ending"
      : action === "extend"
        ? "Extending"
        : "Relisting";
  const verbDone =
    action === "end" ? "Ended" : action === "extend" ? "Extended" : "Relisted";
  return (
    <Modal
      open={open}
      onClose={() => !running && onClose()}
      ariaLabel={`Bulk ${action} progress`}
      panelClassName="w-[min(540px,92vw)] max-h-[85vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">
          {running
            ? `${verb} ${done}/${total}…`
            : `${verbDone} ${succeeded}/${total}`}
        </h2>
        <button
          type="button"
          onClick={onClose}
          disabled={running}
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-2 overflow-y-auto p-4">
        {results.map((r) => (
          <div
            key={r.listingId}
            className={
              r.ok
                ? "flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-xs"
                : "flex items-start gap-2 rounded-md border border-destructive/60 bg-destructive/10 p-2 text-xs"
            }
          >
            {r.ok ? (
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
            ) : (
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-foreground">
                {r.title}
              </div>
              {!r.ok && r.error && (
                <div className="text-[11px] text-destructive">{r.error}</div>
              )}
            </div>
          </div>
        ))}
        {running && results.length < total && (
          <div className="rounded-md border border-dashed border-border bg-muted p-2 text-center text-xs text-muted-foreground">
            Working through {total - results.length} more…
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          disabled={running}
          className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          {running ? "Working…" : "Close"}
        </button>
      </div>
    </Modal>
  );
}
